import { useLazyLoadQuery } from 'react-relay';
import { BlockStack, Box, PageHeader } from '@pv-frontend/ui-components-web';
import { featureFlagsQuery } from '../relay/feature-flags-query';
import type { featureFlagsQuery as FeatureFlagsQuery } from '../relay/__generated__/featureFlagsQuery.graphql';
import { useFeatureFlagDials } from '../relay/use-feature-flag-dials';
import { FeatureFlagList, type FeatureFlagView } from './feature-flag-list';

/**
 * Renders the live `Apis.Admin` feature-flag list with its operational dials
 * (Stage / Kill / Percent), authenticated as the operator. Suspends while the
 * query is in flight; the surrounding `Suspense` boundary shows the cold-start
 * "waking…" state (see `app.tsx`). Dial mutations run through
 * `useFeatureFlagDials` for optimistic UI + success/error toasts.
 */
export function FlagsScreen() {
    const data = useLazyLoadQuery<FeatureFlagsQuery>(
        featureFlagsQuery,
        {},
        { fetchPolicy: 'store-or-network' },
    );

    const baseFlags: FeatureFlagView[] = data.featureFlags.map((flag) => ({
        key: flag.key,
        stage: flag.stage,
        killed: flag.killed,
        percent: flag.percent,
        kind: flag.kind,
    }));

    const { flags, busyKeys, onSetStage, onSetKilled, onSetPercent } = useFeatureFlagDials(baseFlags);

    return (
        <Box padding="600" maxWidth={880}>
            <BlockStack gap="500">
                <PageHeader
                    title="Feature flags"
                    description="Live flags from the Admin API, as seen by your operator account."
                />
                <FeatureFlagList
                    flags={flags}
                    busyKeys={busyKeys}
                    onSetStage={onSetStage}
                    onSetKilled={onSetKilled}
                    onSetPercent={onSetPercent}
                />
            </BlockStack>
        </Box>
    );
}
